// Colours of the game table (the "tapis"). Each player picks their own, from the
// Lobby's "Tapis" button or the waiting room (components/TableColorPicker.jsx);
// the choice is kept on their device (localStorage) and paints their `.rami-mat`
// in every game (Game.jsx), solo games included. Names: `room.table.names.<id>`
// in lib/i18n.
//
// Each colour is an "r, g, b" triplet for the CSS variables of `.rami-mat`
// (index.css): the light in the middle, the felt, and the dark edge. The light
// is see-through (glowAlpha, 0.35 by default): glow × alpha should come close to
// `mid`, or the middle of the table looks darker than the felt around it.
import { useRef, useSyncExternalStore } from "react";
import { Capacitor } from "@capacitor/core";
import { TABLE_LOCKS } from "./features";

export const DEFAULT_TABLE_THEME = "emeraude";

export const TABLE_THEMES = [
  { id: "emeraude", glow: "16, 122, 87", mid: "5, 46, 36", edge: "2, 22, 18" },
  { id: "bleu-nuit", glow: "52, 96, 180", mid: "12, 26, 58", edge: "3, 9, 24" },
  { id: "bordeaux", glow: "168, 40, 70", mid: "62, 12, 26", edge: "26, 4, 10" },
  { id: "neon", glow: "124, 58, 237", mid: "26, 12, 60", edge: "8, 4, 22" },
  { id: "noir-or", glow: "201, 162, 39", mid: "16, 15, 14", edge: "5, 5, 6", glowAlpha: 0.16 },
  { id: "mediterranee", glow: "24, 150, 190", mid: "8, 64, 88", edge: "2, 26, 38", glowAlpha: 0.45 },
];

export const tableTheme = (id) =>
  TABLE_THEMES.find((th) => th.id === id) || TABLE_THEMES[0];

// Inline style for any element with the `rami-mat` class.
export const matStyle = (id) => {
  const th = tableTheme(id);
  return {
    "--mat-glow": th.glow,
    "--mat-glow-alpha": th.glowAlpha ?? 0.35,
    "--mat-mid": th.mid,
    "--mat-edge": th.edge,
  };
};

const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// ── Colours unlocked with a video ───────────────────────────────────────────
// The green table is always free. The others are locked (TABLE_LOCKS in
// lib/features.js) until the player watches a rewarded video in the app
// (lib/ads.js → lib/tableUnlocks.js). A colour is then theirs for 1 hour,
// renewed by the server while they sit in a started game, locked again
// otherwise (backend/table_unlocks.py). The server keeps the unlocks on the
// account; this is the page's copy, {id: end time in ms}, also kept in
// localStorage so the table does not flash green before the server answers.
const UNLOCKS_KEY = "rami_table_unlocks";
let unlocks = null;
let expiryTimer = null;
let refreshOnExpiry = null; // lib/tableUnlocks.js: asks the server, which may renew

// The locks are for the Android / iOS apps only: on the website every colour is
// free (owner's choice; the website has no video to unlock them anyway).
const LOCKED_HERE = TABLE_LOCKS && Capacitor.isNativePlatform();

export const isTableThemeFree = (id) => !LOCKED_HERE || tableTheme(id).id === DEFAULT_TABLE_THEME;

function readUnlocks() {
  if (unlocks === null) {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(UNLOCKS_KEY)) || {}; } catch { /* none */ }
    setTableUnlocks(saved, { quiet: true });
  }
  return unlocks;
}

/**
 * Replaces the unlocks with the server's list ({id: ISO end time}, or ms).
 * Those already over are left out.
 */
export function setTableUnlocks(map, { quiet = false } = {}) {
  const now = Date.now();
  const next = {};
  for (const [id, end] of Object.entries(map || {})) {
    const ms = typeof end === "number" ? end : Date.parse(end);
    if (ms > now && !isTableThemeFree(id)) next[id] = ms;
  }
  unlocks = next;
  try { localStorage.setItem(UNLOCKS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  // At the first end, ask the server again: it renews the colour if a game is
  // on, or it is gone. Without an answer, the colour locks here.
  clearTimeout(expiryTimer);
  const first = Math.min(...Object.values(next));
  if (Number.isFinite(first)) {
    expiryTimer = setTimeout(() => {
      const prune = () => setTableUnlocks(unlocks);
      if (refreshOnExpiry) refreshOnExpiry().catch(prune);
      else prune();
    }, first - now + 1000);
  }
  if (!quiet) emit();
}

export const setTableUnlockRefresh = (fn) => { refreshOnExpiry = fn; };

export const isTableThemeUnlocked = (id) => isTableThemeFree(id) || id in readUnlocks();

// {id: end time in ms} of the colours unlocked now (same object until it changes).
export const useTableUnlocks = () => useSyncExternalStore(subscribe, readUnlocks, readUnlocks);

// ── The player's own choice ─────────────────────────────────────────────────
const KEY = "rami_table_theme";
let current = null;

// The colour the player picked, unlocked or not.
export function getChosenTableTheme() {
  if (current === null) {
    try { current = localStorage.getItem(KEY); } catch { current = undefined; }
  }
  return tableTheme(current).id;
}

// The colour their table has: the one they picked, or green while it is locked.
export function getMyTableTheme() {
  const id = getChosenTableTheme();
  return isTableThemeUnlocked(id) ? id : DEFAULT_TABLE_THEME;
}

export function setMyTableTheme(id) {
  current = tableTheme(id).id;
  try { localStorage.setItem(KEY, current); } catch { /* private mode: kept for this visit */ }
  emit();
}

// The player's colour id, re-rendering when they pick another or it locks.
export const useMyTableTheme = () => useSyncExternalStore(subscribe, getMyTableTheme, getMyTableTheme);

/**
 * The colour of a game page. A colour that runs out during the game stays
 * until the page leaves: the server renews it while the game is on (+1 h), and
 * the table must not turn green under the player in the meantime.
 */
export function useGameTableTheme() {
  const chosen = useSyncExternalStore(subscribe, getChosenTableTheme, getChosenTableTheme);
  const shown = useMyTableTheme();
  const kept = useRef(new Set());
  if (shown === chosen) kept.current.add(chosen);
  return kept.current.has(chosen) ? chosen : shown;
}
